import {Platform, PlatformDataType} from 'src/interfaces/Platform.interface'

const SHOPIFY_USER_SOURCE_PREFIX = 'SHOPIFY'

/**
 * True when authenticated-user `source` is SHOPIFY or a Shopify bundle token only, e.g.
 * SHOPIFY, SHOPIFY_ODOO, SHOPIFY-foo, SHOPIFY0000. Does not match unrelated strings.
 */
export function isShopifyFamilyUserSource(source?: string | null): boolean {
  if (source == null || typeof source !== 'string') {
    return false
  }
  const normalized = source.trim().toUpperCase()
  if (normalized === SHOPIFY_USER_SOURCE_PREFIX) {
    return true
  }
  if (!normalized.startsWith(SHOPIFY_USER_SOURCE_PREFIX)) {
    return false
  }
  const suffix = normalized.slice(SHOPIFY_USER_SOURCE_PREFIX.length)
  if (suffix.length === 0) {
    return true
  }
  if (suffix.startsWith('_') || suffix.startsWith('-')) {
    return suffix.length > 1
  }
  return /^[A-Z0-9]+$/.test(suffix)
}

/** Confirm Screen A sources: exact match, with SHOPIFY* normalized to SHOPIFY. */
export function isConfirmPlatformConnectionSource(
  source: string | null | undefined,
  confirmSources: readonly string[]
): boolean {
  if (!source) {
    return false
  }
  if (isShopifyFamilyUserSource(source)) {
    return confirmSources.includes('SHOPIFY')
  }
  return confirmSources.includes(source)
}

/** True when the platform record represents a source / primary channel. */
export const isPrimaryPlatform = (platform?: PlatformDataType | null): boolean =>
  Boolean(
    platform?.isMainStore || platform?.isAccountingStore || platform?.isShippingStore
  )

/** Locate the connected primary platform in a platform list (order-independent). */
export const findPrimaryPlatform = (
  platforms: PlatformDataType[] = []
): PlatformDataType | undefined => platforms.find((item) => isPrimaryPlatform(item))

/** Resolve primary platform from list or pre-computed main-store slice value. */
export const resolvePrimaryPlatform = (
  platforms: PlatformDataType[] = [],
  mainStoreData?: PlatformDataType | null
): PlatformDataType | undefined => mainStoreData ?? findPrimaryPlatform(platforms)

export function filterTourPlatforms(
  platforms: Platform[] | undefined | null,
  allowedNatures: readonly string[],
  searchTerm?: string
): Platform[] {
  const byNature = Array.isArray(platforms)
    ? platforms.filter((platform) => allowedNatures.includes(platform.nature))
    : []

  const term = (searchTerm ?? '').trim().toLowerCase()
  if (!term) {
    return byNature
  }

  return byNature.filter(
    (platform) =>
      `${platform?.name ?? ''}`.toLowerCase().includes(term) ||
      `${platform?.display_name ?? ''}`.toLowerCase().includes(term)
  )
}
